import { isApiError } from '@botpress/client'
import { getApiClient } from '~/stores'

import { z } from 'zod'
import { analyticsBoardConfigSchema } from '~/features/analytics'
import { defaultChartLayout } from '~/features/analytics/constants'
import { PathParams, PreferenceConfig, resolveKey } from './preferences'

/*
 * Warning! This is the source of truth for preference config.
 * Do not update the preference with any breaking changes otherwise it will break the app for users with the old preferences.
 * If you need to update the preference, create a new preference and migrate the old preference to the new one.
 */
const preferenceConfig = {
  // This key is used to avoid a typing error while accessing props.path
  // It would otherwise be a never type
  fakeKey: {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
  onBoardingCompleted: {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
  'analyticsGridBoard/$botId': {
    schema: z.lazy(() => analyticsBoardConfigSchema),
    default: defaultChartLayout,
  },
  'isOnboarding/$botId': {
    schema: z
      .union([
        z.boolean(),
        z.object({
          entryPoint: z.string(),
        }),
      ])
      .catch(() => false),
    default: false,
    error: false,
  },
  'isAdkOnboarding/$botId': {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
  'isStudioOnboarding/$botId': {
    schema: z
      .object({
        entryPoint: z.string(),
        version: z.string(),
      })
      .catch(() => ({ entryPoint: 'new_bot', version: 'default' })),
    default: false,
    error: false,
  },
  // Old state : do not remove or reuse
  // downgradeToCommunity: {
  //   schema: z.boolean().catch(() => false),
  //   default: false,
  //   error: false,
  // },
  cancelledToCommunity: {
    schema: z
      .object({
        value: z.boolean(),
        date: z.date().optional(),
      })
      .catch(() => ({ value: false })),
    default: { value: false },
    error: { value: false },
  },
} as const satisfies { [key: string]: PreferenceConfig }

type WorkspacePreferencesConfig = typeof preferenceConfig
export type WorkspacePreferences = {
  [K in keyof WorkspacePreferencesConfig]: z.infer<WorkspacePreferencesConfig[K]['schema']>
}

export type GetWorkspacePreferenceProps<T extends keyof WorkspacePreferences> = [PathParams<T>] extends [never]
  ? { path: T }
  : { path: T; params: PathParams<T> }

export type SetWorkspacePreferenceProps<T extends keyof WorkspacePreferences> = GetWorkspacePreferenceProps<T> & {
  value: WorkspacePreferences[T]
}

export type SetWorkspacePreferencePropsUnion = {
  [K in keyof WorkspacePreferences]: SetWorkspacePreferenceProps<K>
}[keyof WorkspacePreferences]

export const getWorkspacePreference = async <T extends keyof WorkspacePreferences>(
  props: GetWorkspacePreferenceProps<T>,
  workspaceId: string
): Promise<WorkspacePreferences[T]> => {
  const client = getApiClient({ workspaceId })

  const pref = await client.getWorkspacePreference({ key: resolveKey(props) }).catch((err) => {
    if (isApiError(err) && err.type === 'ResourceNotFound') {
      return { value: preferenceConfig[props.path].default }
    }
    throw err
  })

  return pref.value
}

export const setWorkspacePreference = async (props: SetWorkspacePreferencePropsUnion, workspaceId: string) => {
  const client = getApiClient({ workspaceId })
  const params = 'params' in props ? (props.params as Record<string, string>) : {}

  await client.setWorkspacePreference({
    key: resolveKey({ path: props.path, params: { ...params, workspaceId } }),
    value: props.value,
  })
}
